import { useTranslation } from "react-i18next";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SchemaOrg, getBreadcrumbSchema, getFAQSchema, getOrganizationSchema, getWebSiteSchema } from "@/components/seo/SchemaOrg";
import { HreflangTags } from "@/components/seo/HreflangTags";
import { getLocalizedUrl } from "@/hooks/useSeoMeta";
import { LocaleKey } from "@/i18n";
import { 
  HelpCircle, 
  FileText, 
  Calendar, 
  CreditCard, 
  RefreshCw, 
  Headphones,
  Building2,
  MapPin,
  Tag,
  Mail,
  Search,
  Hotel,
  BadgeCheck,
  MessageCircle,
  Clock,
  Baby,
  Users,
  UserX,
  Wallet,
  SplitSquareHorizontal,
  Receipt,
  AlertCircle,
  CheckCircle,
  FileX,
  CalendarX,
  RotateCcw,
  Phone,
  FileCheck,
  Info,
  ShieldCheck,
  Store
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLocale } from "@/contexts/LocaleContext";

interface FAQItem {
  question: string;
  answer: string;
  icon: React.ElementType;
}

interface FAQSection {
  key: string;
  title: string;
  icon: React.ElementType;
  items: FAQItem[];
}

const FAQ = () => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  // Define sections with their item keys and icons
  const sectionDefinitions = [
    {
      key: 'content',
      icon: FileText,
      itemKeys: ['howKnow', 'updated', 'problem', 'notFoundDest', 'offers'],
      itemIcons: [MapPin, RefreshCw, Mail, Search, Tag]
    },
    {
      key: 'reservations',
      icon: Calendar,
      itemKeys: ['howReserve', 'whyCheaper', 'hotelGood', 'doubts', 'instantConfirm', 'childDiscount', 'groupDiscount', 'minor'],
      itemIcons: [Hotel, Tag, BadgeCheck, MessageCircle, Clock, Baby, Users, UserX]
    },
    {
      key: 'payments',
      icon: CreditCard,
      itemKeys: ['methods', 'installments', 'fees', 'cardDeclined', 'confirmed', 'invoice'],
      itemIcons: [Wallet, SplitSquareHorizontal, Receipt, AlertCircle, CheckCircle, FileCheck]
    },
    {
      key: 'cancellations',
      icon: RefreshCw,
      itemKeys: ['policy', 'howCancel', 'extraFee'],
      itemIcons: [FileX, CalendarX, RotateCcw]
    },
    {
      key: 'support',
      icon: Headphones,
      itemKeys: ['noEmail', 'duringTrip', 'documents'],
      itemIcons: [Mail, Phone, FileCheck]
    },
    {
      key: 'about',
      icon: Building2,
      itemKeys: ['whoWeAre', 'safe', 'physicalStore'],
      itemIcons: [Info, ShieldCheck, Store]
    }
  ];

  const faqSections: FAQSection[] = sectionDefinitions.map(section => ({
    key: section.key,
    title: t(`faq.sections.${section.key}.title`),
    icon: section.icon,
    items: section.itemKeys.map((itemKey, idx) => ({
      question: t(`faq.sections.${section.key}.${itemKey}.q`),
      answer: t(`faq.sections.${section.key}.${itemKey}.a`),
      icon: section.itemIcons[idx] || HelpCircle
    }))
  }));

  // Flatten FAQs for Schema.org
  const allFaqs = faqSections.flatMap(section => 
    section.items.map(item => ({
      question: item.question,
      answer: item.answer
    }))
  );

  const baseUrl = getLocalizedUrl(locale as LocaleKey, '/');

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: t('nav.home', 'Home'), url: baseUrl },
    { name: t('faq.title'), url: getLocalizedUrl(locale as LocaleKey, '/faq') }
  ]);

  const faqSchema = getFAQSchema(allFaqs);
  const organizationSchema = getOrganizationSchema(locale as LocaleKey);
  const webSiteSchema = getWebSiteSchema(locale as LocaleKey);

  return (
    <>
      <HreflangTags path="/faq" />
<SchemaOrg schema={[organizationSchema, webSiteSchema, breadcrumbSchema, faqSchema]} />

      <div className="min-h-screen bg-background">
        <Navbar />
        
        <main id="main-content" className="container mx-auto px-4 py-12">
          {/* Hero Section */}
          <section className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-full bg-primary/10">
                <HelpCircle className="h-16 w-16 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t('faq.title')}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('faq.subtitle')}
            </p>
          </section>

          {/* FAQ Sections */}
          <div className="max-w-4xl mx-auto space-y-8">
            {faqSections.map((section, sectionIndex) => (
              <section key={section.key} className="bg-card rounded-2xl border border-border/50 overflow-hidden">
                <div className="flex items-center gap-4 p-6 bg-muted/30 border-b border-border/50">
                  <div className="p-3 rounded-full bg-primary/10">
                    <section.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">{section.title}</h2>
                </div>
                <Accordion type="single" collapsible className="px-6">
                  {section.items.map((item, itemIndex) => (
                    <AccordionItem key={itemIndex} value={`${sectionIndex}-${itemIndex}`}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3 text-left">
                          <item.icon className="h-5 w-5 text-primary shrink-0" />
                          <span className="font-medium">{item.question}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground pl-8 leading-relaxed">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            ))}
          </div>

          {/* Contact CTA */}
          <section className="max-w-4xl mx-auto mt-12 text-center bg-primary/5 rounded-2xl p-8 border border-primary/20">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-primary/10">
                <Mail className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-2">
              {t('faq.notFound')}
            </h3>
            <p className="text-muted-foreground mb-4">
              {t('faq.notFoundSubtitle')}
            </p>
            <a 
              href="mailto:contato@foradarota.com" 
              className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
            >
              <Mail className="h-4 w-4" />
              contato@foradarota.com
            </a>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default FAQ;
